<template>
    <div>
        <v-btn
            class="ai-chat-fab"
            color="#1f0038"
            dark
            fixed
            right
            @click="drawer = true"
        >
            <v-icon>mdi-robot-outline</v-icon>
        </v-btn>

        <v-navigation-drawer
            v-model="drawer"
            right
            temporary
            fixed
            :width="$vuetify.breakpoint.smAndDown ? '100%' : 480"
            class="ai-chat-drawer"
        >
            <div class="ai-chat-header">
                <div>
                    <div class="ai-chat-title">Chat IA</div>
                    <div class="ai-chat-subtitle">{{ contextLabel }}</div>
                </div>
                <div class="d-flex align-center">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon dark v-bind="attrs" v-on="on" @click="resetMessages">
                                <v-icon>mdi-broom</v-icon>
                            </v-btn>
                        </template>
                        <span>Limpar conversa</span>
                    </v-tooltip>
                    <v-btn icon dark @click="drawer = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <div class="ai-chat-body">
                <v-alert v-if="!hasCaseContext" dense text type="info" class="mb-3">
                    A busca inteligente está limitada por caso. Abra um caso ou informe o ID do caso abaixo.
                </v-alert>

                <v-text-field
                    v-if="!hasCaseContext"
                    v-model="manualCaseId"
                    dense
                    outlined
                    hide-details="auto"
                    label="ID do caso"
                    class="mb-3"
                />

                <div class="ai-quick-prompts" v-if="showQuickPrompts">
                    <v-chip
                        v-for="prompt in quickPrompts"
                        :key="prompt"
                        small
                        outlined
                        color="primary"
                        @click="sendQuickPrompt(prompt)"
                    >
                        {{ prompt }}
                    </v-chip>
                </div>

                <div class="ai-chat-messages" ref="messages">
                    <div
                        v-for="(message, index) in messages"
                        :key="`ai-message-${index}`"
                        :class="['ai-message', message.role === 'user' ? 'ai-message-user' : 'ai-message-assistant']"
                    >
                        <div class="ai-message-content">
                            <div class="white-space-pre-line">{{ message.text }}</div>

                            <div v-if="message.summary" class="ai-message-section">
                                <strong>Resumo</strong>
                                <div class="white-space-pre-line">{{ message.summary }}</div>
                            </div>

                            <div v-if="message.warnings && message.warnings.length" class="ai-message-section">
                                <strong>Atenção</strong>
                                <ul>
                                    <li v-for="(item, warningIndex) in message.warnings" :key="`warning-${index}-${warningIndex}`">
                                        {{ item }}
                                    </li>
                                </ul>
                            </div>

                            <div v-if="message.pending_items && message.pending_items.length" class="ai-message-section">
                                <strong>Pendências</strong>
                                <ul>
                                    <li v-for="(item, pendingIndex) in message.pending_items" :key="`pending-${index}-${pendingIndex}`">
                                        {{ item }}
                                    </li>
                                </ul>
                            </div>

                            <div v-if="message.suggested_actions && message.suggested_actions.length" class="ai-message-section">
                                <strong>Ações sugeridas</strong>
                                <ul>
                                    <li v-for="(item, actionIndex) in message.suggested_actions" :key="`action-${index}-${actionIndex}`">
                                        {{ item }}
                                    </li>
                                </ul>
                            </div>

                            <div v-if="message.usage" class="ai-message-meta">
                                <span>{{ message.usage.model || "modelo IA" }}</span>
                                <span v-if="message.usage.total_tokens">{{ message.usage.total_tokens }} tokens</span>
                                <span v-if="message.usage.estimated_cost_usd !== undefined">
                                    US$ {{ Number(message.usage.estimated_cost_usd).toFixed(6) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading" class="ai-message ai-message-assistant">
                        <div class="ai-message-content">
                            <v-progress-circular indeterminate size="18" width="2" color="#7c6ff0" class="mr-2" />
                            Analisando contexto...
                        </div>
                    </div>
                </div>
            </div>

            <div class="ai-chat-input">
                <v-textarea
                    v-model="message"
                    dense
                    outlined
                    auto-grow
                    rows="2"
                    hide-details="auto"
                    placeholder="Pergunte algo sobre este caso"
                    :disabled="loading"
                    @keydown.enter.exact.prevent="sendMessage"
                />
                <v-btn
                    color="#1f0038"
                    dark
                    elevation="0"
                    class="ml-2"
                    :loading="loading"
                    :disabled="!canSend"
                    @click="sendMessage"
                >
                    <v-icon>mdi-send</v-icon>
                </v-btn>
            </div>
        </v-navigation-drawer>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "AIChatFab",

    data() {
        return {
            drawer: false,
            loading: false,
            message: "",
            manualCaseId: "",
            messages: [],
            quickPrompts: [
                "Quais pendências existem neste caso?",
                "Resuma os principais riscos",
                "Verifique pontos de atenção da AGC",
                "Liste próximas ações sugeridas",
            ],
        };
    },

    mounted() {
        this.resetMessages();
    },

    computed: {
        routeContext() {
            const path = this.$route.path || "";
            const match = (pattern) => {
                const result = path.match(pattern);
                return result ? result[1] : null;
            };

            const caseId = match(/^\/casos\/(\d+)/);
            if (caseId) return { module: "client", entity_id: caseId, case_id: caseId, route: path };

            const generalMeetingId = match(/^\/agcs\/(\d+)/);
            if (generalMeetingId) return { module: "general_meeting", entity_id: generalMeetingId, route: path };

            const habDivId = match(/^\/divergencias\/(\d+)/);
            if (habDivId) return { module: "hab_div", entity_id: habDivId, route: path };

            const officialLetterId = match(/^\/analise-oficio\/(\d+)/);
            if (officialLetterId) return { module: "official_letter", entity_id: officialLetterId, route: path };

            const procuratorId = match(/^\/procuradores\/(\d+)/);
            if (procuratorId) return { module: "procurator", entity_id: procuratorId, route: path };

            return { module: "general", entity_id: null, route: path };
        },

        inferredCaseId() {
            return this.routeContext.case_id || null;
        },

        selectedCaseId() {
            return this.inferredCaseId || this.manualCaseId || null;
        },

        hasCaseContext() {
            return !!this.selectedCaseId || ["general_meeting", "hab_div", "official_letter", "accreditation", "procurator_association"].includes(this.routeContext.module);
        },

        contextLabel() {
            if (this.inferredCaseId) return `Caso #${this.inferredCaseId}`;
            if (this.routeContext.module !== "general") return `Contexto: ${this.routeContext.module} #${this.routeContext.entity_id}`;
            return "Limitado por caso";
        },

        canSend() {
            return !!String(this.message || "").trim() && !this.loading;
        },

        showQuickPrompts() {
            return this.messages.length <= 1 && !this.loading;
        },
    },

    methods: {
        resetMessages() {
            this.messages = [
                {
                    role: "assistant",
                    text: "Olá. Posso ajudar a resumir pendências, riscos e informações do contexto atual.",
                },
            ];
            this.$nextTick(this.scrollToBottom);
        },

        formatAssistantMessage(content) {
            return {
                role: "assistant",
                text: content.answer || content.summary || "Não foi possível montar uma resposta.",
                summary: content.answer && content.summary && content.summary !== content.answer ? content.summary : null,
                warnings: content.warnings || [],
                pending_items: content.pending_items || [],
                suggested_actions: content.suggested_actions || [],
                usage: content.usage || null,
            };
        },

        sendQuickPrompt(prompt) {
            this.message = prompt;
            this.sendMessage();
        },

        scrollToBottom() {
            const container = this.$refs.messages;
            if (container && container.scrollIntoView) {
                container.scrollIntoView({ block: "end" });
            }
        },

        async sendMessage() {
            if (!this.canSend) return;

            const currentMessage = String(this.message || "").trim();
            this.message = "";
            this.messages.push({ role: "user", text: currentMessage });
            this.loading = true;
            this.$nextTick(this.scrollToBottom);

            try {
                const response = await axios.post("/api/admin/ai/chat", {
                    message: currentMessage,
                    case_id: this.selectedCaseId,
                    context: this.routeContext,
                });

                this.messages.push(this.formatAssistantMessage(response.data.content || {}));
            } catch (error) {
                const data = error.response && error.response.data ? error.response.data : {};
                const message = data.error && data.error.message
                    ? data.error.message
                    : data.content && data.content.error && data.content.error.message
                        ? data.content.error.message
                        : "Não foi possível consultar a IA.";

                this.messages.push({
                    role: "assistant",
                    text: message,
                    warnings: [],
                    pending_items: [],
                    suggested_actions: [],
                    usage: null,
                });
            } finally {
                this.loading = false;
                this.$nextTick(this.scrollToBottom);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.ai-chat-fab {
    z-index: 20;
    right: 0 !important;
    top: 50% !important;
    bottom: auto !important;
    transform: translateY(-50%);
    min-width: 44px !important;
    width: 44px !important;
    height: 64px !important;
    border-radius: 12px 0 0 12px !important;
    box-shadow: 0 10px 24px rgba(31, 0, 56, 0.28) !important;
}

@media (max-width: 600px) {
    .ai-chat-fab {
        top: auto !important;
        right: 16px !important;
        bottom: 92px !important;
        transform: none;
        width: 52px !important;
        height: 52px !important;
        border-radius: 8px !important;
    }
}

.ai-chat-drawer {
    z-index: 30;
}

.ai-chat-header {
    min-height: 76px;
    padding: 16px;
    background: #1f0038;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ai-chat-title {
    font-size: 18px;
    font-weight: 700;
}

.ai-chat-subtitle {
    font-size: 12px;
    opacity: 0.78;
}

.ai-chat-body {
    height: calc(100vh - 166px);
    padding: 14px;
    overflow-y: auto;
    background: #f6f6fa;
}

.ai-quick-prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
}

.ai-chat-messages {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ai-message {
    display: flex;
}

.ai-message-user {
    justify-content: flex-end;
}

.ai-message-assistant {
    justify-content: flex-start;
}

.ai-message-content {
    max-width: 92%;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.5;
    background: #ffffff;
    color: #29283a;
    border: 1px solid #e4e4ec;
}

.ai-message-user .ai-message-content {
    background: #1f0038;
    border-color: #1f0038;
    color: #ffffff;
}

.ai-message-section {
    margin-top: 10px;

    ul {
        margin: 4px 0 0;
        padding-left: 18px;
    }
}

.ai-message-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
    font-size: 11px;
    opacity: 0.72;
}

.ai-chat-input {
    height: 90px;
    padding: 12px;
    display: flex;
    align-items: flex-end;
    border-top: 1px solid #e4e4ec;
    background: #ffffff;
}

.white-space-pre-line {
    white-space: pre-line;
}
</style>
